import React, { useState, useEffect } from 'react';
import axios from './axiosInstance';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import './styles/SolumKnowledgeBase.css';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import mammoth from 'mammoth';
import ProtectedComponent from './ProtectedComponent';

// Set the workerSrc to the locally hosted file
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const SolumKnowledgeBase = () => {
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('');
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [activeFile, setActiveFile] = useState(null);
  const [docxContent, setDocxContent] = useState('');

  // Search-related state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [expandedResults, setExpandedResults] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [showResultsBox, setShowResultsBox] = useState(true);

  // Modal + upload-related state
  const [showModal, setShowModal] = useState(false);
  const [draggedFiles, setDraggedFiles] = useState([]);
  const [uploadMessage, setUploadMessage] = useState('');
  const [fileExistsMessage, setFileExistsMessage] = useState([]);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const response = await axios.get('/files');
      setFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const handleFileClick = async (file, targetPageNumber = 1) => {
    const fileExtension = file.split('.').pop().toLowerCase();
    setFileType(fileExtension);
    setActiveFile(file);

    try {
      const fileResponse = await axios.get(`/files/${file}`, {
        responseType: 'blob',
      });

      const fileURL = URL.createObjectURL(fileResponse.data);

      if (fileExtension === 'pdf') {
        setSelectedFile(fileURL);
        setPageNumber(targetPageNumber);
      } else if (fileExtension === 'docx') {
        const arrayBuffer = await fileResponse.data.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setDocxContent(result.value);
      }
    } catch (error) {
      console.error('Error fetching the file:', error);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  /**
   * 1) Just update searchTerm and let the debounced effect (below) run the actual search.
   */
  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
  };

  /**
   * 2) Debounce the actual call to 'handleSearch'.
   *    - If user clears the input, reset results.
   *    - If non-empty, wait 500 ms of inactivity, then call handleSearch.
   */
  useEffect(() => {
    const term = searchTerm.trim();

    if (!term) {
      // If the user cleared input, reset search
      setSearchResults([]);
      setShowResultsBox(false);
      return;
    }

    // Indicate we're searching as soon as they start typing
    setIsSearching(true);

    // Wait 500ms after the user stops typing
    const timeoutId = setTimeout(() => {
      handleSearch(term);
    }, 500);

    // If user types again before 500ms pass, cancel the previous search
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  /**
   * 3) The actual PDF search logic remains the same,
   *    except we rely on the debounced call from the useEffect above.
   */
  const handleSearch = async (term) => {
    // If empty string somehow gets here, just reset
    if (term === '') {
      setSearchResults([]);
      setShowResultsBox(false);
      setIsSearching(false);
      return;
    }

    const results = [];

    try {
      const promises = files
        .filter((file) => file.endsWith('.pdf'))
        .map(async (file) => {
          const fileURL = await axios.get(`/files/${file}`, {
            responseType: 'blob',
          });
          const url = URL.createObjectURL(fileURL.data);

          const loadingTask = pdfjs.getDocument(url);
          const pdf = await loadingTask.promise;

          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map((item) => item.str).join(' ');

            const keywordIndex = pageText.toLowerCase().indexOf(term.toLowerCase());
            if (keywordIndex !== -1) {
              const contextLength = 50;
              const snippetStart = Math.max(keywordIndex - contextLength, 0);
              const snippetEnd = Math.min(
                keywordIndex + term.length + contextLength,
                pageText.length
              );
              const snippet = pageText.substring(snippetStart, snippetEnd);

              results.push({
                file,
                pageNum,
                snippet,
              });
            }
          }
        });

      await Promise.all(promises);
    } catch (error) {
      console.error('Error searching PDFs:', error);
    }

    setSearchResults(results);
    setIsSearching(false);
    setShowResultsBox(results.length > 0);
  };

  const toggleExpandResults = (file) => {
    setExpandedResults((prevState) => ({
      ...prevState,
      [file]: !prevState[file],
    }));
  };

  const handleResultClick = (file, pageNum) => {
    setExpandedResults((prevState) => ({
      ...prevState,
      [file]: true,
    }));
    handleFileClick(file, pageNum);
  };

  const highlightKeyword = (text = '', keyword = '') => {
    if (!text || !keyword) return text;
    const parts = text.split(new RegExp(`(${keyword})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === keyword.toLowerCase() ? (
        <mark key={index}>{part}</mark>
      ) : (
        part
      )
    );
  };

  const handleCloseResultsBox = () => {
    setShowResultsBox(false);
    setSearchTerm('');
    setSearchResults([]);
  };

  // Drag + drop upload stuff below

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);

    if (droppedFiles.length > 0) {
      const validFiles = droppedFiles.filter(
        (file) =>
          file.type === 'application/pdf' ||
          file.type === 'application/msword' ||
          file.type ===
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      );

      setDraggedFiles(validFiles);
      await checkFileExistence(validFiles);
    }
  };

  const checkFileExistence = async (filesToCheck) => {
    try {
      const response = await axios.post(
        '/checkFiles',
        { files: filesToCheck.map((file) => file.name) }
      );

      const { existingFiles } = response.data;
      const updatedMessages = filesToCheck.map((file) =>
        existingFiles.includes(file.name)
          ? `${file.name} - file already exists`
          : `${file.name} ready for upload`
      );

      setFileExistsMessage(updatedMessages);
    } catch (error) {
      console.error('Error checking files:', error);
    }
  };

  const handleFileRemove = (index) => {
    const updatedFiles = [...draggedFiles];
    updatedFiles.splice(index, 1);
    setDraggedFiles(updatedFiles);

    const updatedMessages = [...fileExistsMessage];
    updatedMessages.splice(index, 1);
    setFileExistsMessage(updatedMessages);
  };

  const handleUpload = async () => {
    if (draggedFiles.length === 0) {
      setUploadMessage('No files selected for upload.');
      return;
    }

    const formData = new FormData();
    draggedFiles.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response = await axios.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        setUploadMessage('File(s) uploaded successfully.');
      } else if (response.status === 409) {
        setUploadMessage(`File(s) already exist: ${response.data.message}`);
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setUploadMessage(`File(s) already exist: ${error.response.data.message}`);
      } else {
        setUploadMessage('File upload failed. Please try again.');
      }
    }
  };

  return (
    <ProtectedComponent functionCode="OP01" functionName="Knowledge Base">
      <div className="SolumKnowledgeBase-container">
      <div className="sidebar">
        <h2>SOLUM Knowledge Base</h2>

        <div className="search-container">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchTermChange}
            className="search-input"
          />
          {isSearching && <FaSpinner className="spinner" />}
        </div>

        {showResultsBox && searchResults.length > 0 && (
          <div className="search-results-box">
            <button className="close-btn" onClick={handleCloseResultsBox}>
              <FaTimes />
            </button>
            <ul>
              {files.map((file, index) => {
                const fileOccurrences = searchResults.filter((r) => r.file === file);
                if (fileOccurrences.length === 0) return null;
                return (
                  <li key={index}>
                    <div onClick={() => toggleExpandResults(file)}>
                      {file.replace('.pdf', '')} ({fileOccurrences.length} occurrences)
                    </div>
                    {expandedResults[file] && (
                      <ul>
                        {fileOccurrences.map((result, subIndex) => (
                          <li
                            key={subIndex}
                            onClick={() => handleResultClick(file, result.pageNum)}
                          >
                            Page {result.pageNum} -{' '}
                            {highlightKeyword(result.snippet, searchTerm)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <h3>Files on Server</h3>
        <ul className="file-list">
          {files.length > 0 ? (
            files.map((file, index) => (
              <li
                key={index}
                className={`file-item ${activeFile === file ? 'active' : ''}`}
                onClick={() => handleFileClick(file)}
              >
                {file.replace('.pdf', '')}
              </li>
            ))
          ) : (
            <p>No files found.</p>
          )}
        </ul>

        <div className="tabs">
          <button className="upload-btn" onClick={() => setShowModal(true)}>
            Upload
          </button>
        </div>
      </div>

      <div className="main-content">
        {fileType === 'pdf' && selectedFile && (
          <div className="pdf-viewer">
            <div className="controls">
              <div className="pagination-controls">
                <span>Zoom: {Math.round(scale * 100)}%</span>
                <button onClick={() => setScale((prev) => Math.max(prev - 0.25, 0.5))}>
                  -
                </button>
                <button onClick={() => setScale((prev) => Math.min(prev + 0.25, 3))}>
                  +
                </button>
              </div>

              <div className="page-controls">
                <button
                  onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                  disabled={pageNumber <= 1}
                >
                  {'<<'}
                </button>
                <input
                  type="number"
                  value={pageNumber}
                  onChange={(e) => setPageNumber(Number(e.target.value))}
                  style={{ width: '40px', textAlign: 'center', margin: '0 5px' }}
                />
                <span> of {numPages} </span>
                <button
                  onClick={() => setPageNumber((prev) => Math.min(prev + 1, numPages))}
                  disabled={pageNumber >= numPages}
                >
                  {'>>'}
                </button>
                <button onClick={() => window.open(selectedFile)} className="download-btn">
                  Download
                </button>
              </div>
            </div>

            <Document file={selectedFile} onLoadSuccess={onDocumentLoadSuccess}>
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Document>
          </div>
        )}

        {fileType === 'docx' && docxContent && (
          <div className="docx-viewer">
            <div dangerouslySetInnerHTML={{ __html: docxContent }} />
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal">
          <div
            className="modal-content"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <h3>Upload PDF or DOC/DOCX</h3>
            <div className="drag-drop-box">
              {draggedFiles.length > 0 ? (
                <ul>
                  {draggedFiles.map((file, index) => (
                    <li key={index}>
                      {fileExistsMessage[index] || file.name}
                      <button onClick={() => handleFileRemove(index)}>
                        <FaTimes />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Drag and drop your files here</p>
              )}
            </div>
            <button className="upload-btn" onClick={handleUpload}>
              Upload
            </button>
            {uploadMessage && <p className="upload-message">{uploadMessage}</p>}
            <button className="close-btn" onClick={() => setShowModal(false)}>
              <FaTimes />
            </button>
          </div>
        </div>
      )}
    </div>
    </ProtectedComponent>
  );
};

export default SolumKnowledgeBase;
